/* remote-collection-discounts.js
 * Fetches product->collections mapping from remote JSON and applies discount banners & price updates.
 * Depends on discounter.js (updatePrices, addDiagonalDiscountBanner).
 */
(function () {
	const REMOTE_URL = 'https://earlyaccessapi.dev.artslabcreatives.com/products_collections.json';

	// Discount rules (collection handle -> percentage). Assumption: foot-wear = 25% (adjust if needed).
	const DISCOUNT_RULES = {
		'clearance-sale': 70,
		'casual-wear-1': 30,
		'office-wear': 20,
		'party-wear': 50,
		'foot-wear': 25,
		'hand-bags': 5
	};

	let remoteData = null;
	let fetchInFlight = false;

	function normalize(str) {
		return (str || '')
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, ' ') // drop punctuation
			.replace(/\s+/g, ' ') // collapse spaces
			.trim();
	}

	// Simple similarity score: longest common substring length / avg length
	function similarity(a, b) {
		a = normalize(a); b = normalize(b);
		if (!a || !b) return 0;
		const m = Array(a.length + 1).fill(null).map(() => Array(b.length + 1).fill(0));
		let longest = 0;
		for (let i = 1; i <= a.length; i++) {
			for (let j = 1; j <= b.length; j++) {
				if (a[i - 1] === b[j - 1]) {
					m[i][j] = m[i - 1][j - 1] + 1;
					if (m[i][j] > longest) longest = m[i][j];
				}
			}
		}
		const avgLen = (a.length + b.length) / 2;
		return longest / avgLen;
	}

	function findBestKey(productName, keys) {
		const nameNorm = normalize(productName);
		let best = null; let bestScore = 0;
		keys.forEach(k => {
			const s = similarity(nameNorm, k);
			if (s > bestScore) { bestScore = s; best = k; }
		});
		// Require a minimum threshold to avoid random matches
		return bestScore >= 0.25 ? best : null; // threshold chosen heuristically
	}

	function applyDiscounts() {
		if (!remoteData) return;
		const productNameAnchors = document.querySelectorAll('.wishlist-hero-list-card-header-product-name');
		if (!productNameAnchors.length) return;

		const keys = Object.keys(remoteData);

		productNameAnchors.forEach(anchor => {
			const productName = anchor.textContent.trim();
			if (!productName) return;

			const bestKey = findBestKey(productName, keys);
			if (!bestKey) return;

			const collections = remoteData[bestKey] || [];
			if (!Array.isArray(collections) || !collections.length) return;

			// Determine highest applicable discount
			let appliedDiscount = 0;
			collections.forEach(col => {
				const handle = normalize(col).replace(/\s+/g, '-'); // approximate handle normalization
				Object.keys(DISCOUNT_RULES).forEach(ruleHandle => {
					// partial match both directions for flexibility
					if (handle.includes(ruleHandle) || ruleHandle.includes(handle)) {
						appliedDiscount = Math.max(appliedDiscount, DISCOUNT_RULES[ruleHandle]);
					}
				});
			});

			if (appliedDiscount <= 0) return;

			// Card root: choose closest card-like container
			const cardRoot = anchor.closest('.MuiCard-root1') || anchor.closest('.card-wrapper') || anchor.parentElement;
			if (cardRoot) {
				if (typeof window.addDiagonalDiscountBanner === 'function') {
					window.addDiagonalDiscountBanner(cardRoot, appliedDiscount, appliedDiscount + '% OFF');
				} else {
					// fallback banner creation if main helper missing
					let banner = cardRoot.querySelector(':scope > .diagonal-discount-banner');
					if (!banner) {
						banner = document.createElement('div');
						banner.className = 'diagonal-discount-banner';
						banner.textContent = appliedDiscount + '% OFF';
						cardRoot.style.position = cardRoot.style.position === 'static' ? 'relative' : cardRoot.style.position;
						cardRoot.appendChild(banner);
					} else {
						banner.textContent = appliedDiscount + '% OFF';
					}
				}
			}

			// price container
			const priceContainer = cardRoot.querySelector('.wishlist-hero-price-update, .price__container, .money') || cardRoot;
			if (priceContainer) {
				priceContainer.setAttribute('data-discount-percent', String(appliedDiscount));
			}
		});

		if (typeof window.updatePrices === 'function') window.updatePrices();
	}

	function fetchRemote() {
		if (fetchInFlight || remoteData) return;
		fetchInFlight = true;
		fetch(REMOTE_URL, { credentials: 'omit' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
			.then(json => { remoteData = json; applyDiscounts(); })
			.catch(err => { console.error('Remote discount fetch failed:', err); })
			.finally(() => { fetchInFlight = false; });
	}

	// Public API for manual refresh
	window.applyRemoteCollectionDiscounts = function () {
		if (remoteData) { applyDiscounts(); } else { fetchRemote(); }
	};

	// Initial load strategy
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', fetchRemote);
	} else {
		fetchRemote();
	}

	// --- Dynamic re-application improvements ---
	// Instead of a slow repeating 10s interval, we use:
	// 1. MutationObserver for added/changed product cards
	// 2. Lightweight click & custom events to trigger re-checks
	// 3. A fast short burst retry sequence immediately after initial fetch

	const DEBOUNCE_MS = 120;
	let debounceTimer = null;
	function scheduleApply(reason) {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => { applyDiscounts(); }, DEBOUNCE_MS);
	}

	// Observe DOM changes for new cards or price containers
	const observer = new MutationObserver(mutations => {
		for (const m of mutations) {
			if (m.addedNodes && m.addedNodes.length) {
				for (const n of m.addedNodes) {
					if (!(n instanceof HTMLElement)) continue;
					if (n.matches && (n.matches('.MuiCard-root') || n.matches('.wishlist-hero-list-card-header-product-name') || n.querySelector('.wishlist-hero-list-card-header-product-name'))) {
						scheduleApply('node-added');
						break;
					}
				}
			}
			if (m.type === 'attributes' && m.target && m.target.classList && (m.target.classList.contains('MuiCard-root') || m.target.classList.contains('wishlist-hero-list-card-header-product-name'))) {
				scheduleApply('attr-changed');
			}
		}
	});

	try {
		observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
	} catch (e) { }

	// Re-apply after user interactions that might load new wishlist items
	document.addEventListener('click', () => scheduleApply('click'));
	document.addEventListener('change', () => scheduleApply('change'));
	window.addEventListener('applyRemoteCollectionDiscounts:refresh', () => scheduleApply('custom-event'));

	// Short burst retries (faster than prior 10s interval)
	let burstCount = 0;
	const burstMax = 5;
	(function burst() {
		if (burstCount++ >= burstMax) return;
		setTimeout(() => { applyDiscounts(); burst(); }, 800); // 0.8s spacing
	})();
})();

jQuery('label[for="Filter-filter.p.product_type-10"] .facet-checkbox__text-label')
    .text('70% Sale');
setInterval(function() {
  jQuery('label[for="Filter-filter.p.product_type-10"] .facet-checkbox__text-label')
    .text('70% Sale');
	console.log("70%")
}, 3000);
